import { Quote } from '@1debit/icons';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import { TestimonialItemProps } from 'libs/contentful/props';
import Media from 'components/shared/Media/Media';
import Schema from 'components/shared/Schema/Schema';
import { getRichTextValue } from 'utils/formatters';
import { cn, getColorway } from 'utils/styles';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { TestimonialVariants } from './constants';
import FiveStars from 'components/shared/Icons/FiveStar';
import Link from 'components/shared/Link/Link';

const TestimonialItem = ({
  sys,
  variant,
  copy,
  attributionRte,
  media,
  cta,
  aspectRatio,
  index,
  slideColor,
}: TestimonialItemProps) => {
  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'SoftwareApplication',
      name: 'Chime',
      applicationCategory: 'FinanceApplication',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
    },
    reviewBody: getRichTextValue(copy),
    author: {
      name: attributionRte ? getRichTextValue(attributionRte) : 'Chime Member',
      '@type': 'Person',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Chime',
    },
  };
  if (variant === TestimonialVariants.Cards) {
    return (
      <div
        className={cn(
          getColorway(slideColor),
          'flex flex-col flex-[0_0_100%] md:basis-[375px] shadow-[0_4px_12px_rgba(0,0,0,0.15)] p-6 pt-12 justify-between gap-9 rounded-[16px] md:rounded-[24px]',
          index % 2 === 0 ? 'sm:min-h-[465px]' : 'sm:min-h-[375px]',
        )}>
        <div className="flex flex-col gap-4 items-start">
          <div className="scale-80 origin-left">
            <FiveStars />
          </div>
          <div className="font-serif text-[24px] leading-[140%] font-normal" {...liveUpdateAttrs(sys?.id, 'copy')}>
            <RichTextCopy copy={copy} />
          </div>
        </div>
        <div className="flex flex-row justify-between">
          {attributionRte && (
            <div className="font-sans font-medium text-[16px]" {...liveUpdateAttrs(sys?.id, 'attributionRte')}>
              <RichTextCopy copy={attributionRte} />
            </div>
          )}
          <div className="flex flex-row gap-2 text-[16px]">
            {cta && (
              <div {...liveUpdateAttrs(sys?.id, 'cta')}>
                <Link href={cta?.url} internalPage={cta?.internalPage} className="font-sans font-medium">
                  {cta?.label}
                </Link>
              </div>
            )}
            {media && (
              <div className="relative w-[20px] h-[20px]" {...liveUpdateAttrs(sys?.id, 'media')}>
                <Media media={{ ...media, fill: true }} className="object-contain" sizes="20px" roundedImage={false} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col sm:flex-row gap-[36px] md:gap-[72px] items-center flex-[0_0_100%]"
      role="group"
      aria-roledescription={`slide${index}`}
      data-testid="testimonialItem">
      {media && (
        <div
          className={cn(
            'w-full relative',
            'aspect-square max-w-[300px]', // mobile
            'sm:aspect-video sm:max-w-[400px] sm:h-[267px] ml:max-w-[500px] ml:h-[333px]', // tablet and up
          )}
          data-testid="testimonialMedia"
          {...liveUpdateAttrs(sys?.id, 'media')}>
          <Media
            media={{ ...media, fill: true }}
            className="object-cover"
            sizes="(max-width: 750px) 300px, (max-width: 1200px) 400px, 500px"
            aspectRatio={aspectRatio}
          />
        </div>
      )}
      <blockquote className="font-serif text-lg md:text-2xl">
        <Quote size={32} className="mb-6 hidden md:block" />
        <Quote size={28} className="mb-6 md:hidden" />
        <div {...liveUpdateAttrs(sys?.id, 'copy')} className={`${!media ? 'sm:w-[50%]' : ''}`}>
          <RichTextCopy copy={copy} />
        </div>
        {attributionRte && (
          <div
            className="font-sans text-base md:text-lg font-medium mt-5"
            data-testid="testimonialAttribution"
            {...liveUpdateAttrs(sys?.id, 'attributionRte')}>
            <RichTextCopy copy={attributionRte} />
          </div>
        )}
      </blockquote>
      {jsonLd && <Schema jsonLd={jsonLd} />}
    </div>
  );
};
export default TestimonialItem;
